import { useState } from 'react'
import { Button, Form, Input, Switch, Tag, Space, App } from 'antd'
import { PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { companiesApi } from '@/features/masters/companies/api'
import { getApiError } from '@/shared/api/client'
import type { Company } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

export default function CompaniesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Company | null>(null)

  const canCreate = usePermission('company.create')
  const canUpdate = usePermission('company.update')

  const { data, isLoading } = useQuery({
    queryKey: ['companies', page, search],
    queryFn: () => companiesApi.list({ page, page_size: 20, search: search || undefined }),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldValue('is_active', true)
    setDrawerOpen(true)
  }

  const openEdit = (record: Company) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Omit<Company, 'id'>) =>
      editing ? companiesApi.update(editing.id, values) : companiesApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies'] })
      message.success(editing ? 'Company updated' : 'Company created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (error: unknown) => message.error(getApiError(error, 'Save failed')),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'GSTIN', dataIndex: 'gstin', key: 'gstin', render: (v?: string) => v ?? '—' },
    { title: 'IEC Code', dataIndex: 'iec_code', key: 'iec_code', render: (v?: string) => v ?? '—' },
    { title: 'CIN', dataIndex: 'cin', key: 'cin', render: (v?: string) => v ?? '—' },
    { title: 'State', dataIndex: 'state', key: 'state', render: (v?: string) => v ?? '—' },
    { title: 'Country', dataIndex: 'country', key: 'country' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Company) =>
        canUpdate ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Companies"
        subtitle="Manage company master records"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Company
            </Button>
          )
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by name…"
          allowClear
          onSearch={(v) => { setSearch(v); setPage(1) }}
          style={{ width: 280 }}
        />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={20}
        onPageChange={(p) => setPage(p)}
      />

      <FormDrawer
        title={editing ? 'Edit Company' : 'New Company'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
      >
        <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="gstin" label="GSTIN" rules={[{ len: 15, message: 'GSTIN must be exactly 15 characters' }]}>
          <Input maxLength={15} />
        </Form.Item>
        <Form.Item name="iec_code" label="IEC Code" rules={[{ len: 10, message: 'IEC Code must be exactly 10 characters' }]}>
          <Input maxLength={10} />
        </Form.Item>
        <Form.Item name="cin" label="CIN" rules={[{ len: 21, message: 'CIN must be exactly 21 characters' }]}>
          <Input maxLength={21} />
        </Form.Item>
        <Form.Item name="pan" label="PAN" rules={[{ len: 10, message: 'PAN must be exactly 10 characters' }]}>
          <Input maxLength={10} />
        </Form.Item>
        <Form.Item name="state" label="State">
          <Input />
        </Form.Item>
        <Form.Item name="country" label="Country" rules={[{ required: true }]} initialValue="India">
          <Input />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
